import { FC, useCallback, useState } from 'react';
import toast from 'react-hot-toast';

import {
  listCompanyUsers,
  unassignCompanyUser,
} from '@sdk/managers/UsersManager';
import { useAuth } from '@hooks/Auth';
import useSwr, { mutate } from 'swr';
import { CompanyRole } from '@sdk/API';
import Dialog from '@components/elements/Dialog';
import RoleBadge from '@components/elements/RoleBadge';

export interface CompanyMemberListProps {
  showRemove?: boolean;
  onItemClick?: (item: any) => any;
  showToggle?: boolean;
}

const CompanyMemberList: FC<CompanyMemberListProps> = ({
  showRemove,
  onItemClick,
  showToggle,
}) => {
  const { dbUser, company, loading } = useAuth();
  const { data: members } = useSwr(
    company?.companyID ? ['/admin/members', company.companyID] : null,
    async () => {
      if (!company) {
        if (loading === false) throw Error('User does not own any company.');
        return;
      }

      const proxies: any = await listCompanyUsers(company.companyID);
      return proxies.data?.result?.items || [];
    },
    {
      onError: (err, key) => {
        console.log('Failed to fetch members', { key }, err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      },
    }
  );

  const [modal, setModal] = useState<boolean>(false);
  const hideModal = useCallback(() => setModal(false), [setModal]);
  const showModal = useCallback(() => setModal(true), [setModal]);
  const [selected, setSelected] = useState<any>();

  const removeMember = async (roleId: string) => {
    try {
      if (!dbUser?.id || !dbUser?.username)
        throw new Error('Unexpected error.');

      if (!company) throw new Error('Failed to get company.');

      await toast.promise(unassignCompanyUser(roleId), {
        loading: 'Removing proxy...',
        success: 'Successfully removed proxy',
        error: 'Failed to remove proxy',
      });

      await Promise.all([
        mutate(['/admin/members', company?.companyID]),
        mutate(['/admin/representatives', company?.companyID]),
        mutate(['/admin/proxies', company?.companyID]),
      ]);
      hideModal();
    } catch (err: any) {
      console.log('Failed to remove company proxy', err);
    }
  };

  console.log('members', members);

  return (
    <>
      <Dialog
        className="text-center text-black bg-yellow"
        title={`Removing user ${selected?.user.username}`}
        content={
          <div>
            <p className="mb-4 text-sm">
              Confirm you would like to remove {selected?.user.firstName} from
              your company
            </p>
          </div>
        }
        open={modal}
        onCancel={hideModal}
        onConfirm={() => {
          removeMember(selected?.id);
        }}
      />
      <div className="flex flex-col divide-opacity-30 divide-y-1 divide-solid ">
        {members && members.length === 0 && <div>No members in company</div>}
        {members?.map((member) => (
          <div
            key={`member-${member.id}`}
            className="flex items-center justify-between py-2"
          >
            <div className="text-left">
              <div>
                <RoleBadge role={member.role} />
              </div>
              <div>
                {member.user.firstName || member.user.lastName
                  ? `
                  ${member.user.firstName}
                  ${member.user.lastName && ` ${member.user.lastName}`}
                  ${member.user.username && ` (${member.user.username})`}
                `
                  : member.user.email}
              </div>
            </div>
            {(showRemove || showToggle) && (
              <div>
                {showRemove &&
                  member.userID !== dbUser?.id &&
                  member.role !== CompanyRole.ADMIN && (
                    <button
                      className="px-2 hover:text-red"
                      onClick={() => {
                        if (member.userID === dbUser?.id) {
                          toast.error('You cannot remove yourself');
                          return;
                        }
                        if (member.role === CompanyRole.ADMIN) {
                          toast.error('You cannot remove an Admin');
                          return;
                        }
                        setSelected(member);
                        showModal();
                      }}
                    >
                      Remove
                    </button>
                  )}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default CompanyMemberList;
